import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { OrderApi, PurchaseApi } from "src/services/Api/Organization/types";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import FileUpload from "go-app/components/ImageForm/FileUpload";
import handleError from "go-app/services/errors";
import FileDisplay from "go-component/components/ImportCsv/FileDisplay";
import { ReactComponent as EmptyImageSVG } from "go-component/images/svg/angle-input.svg";
import { selectOrganization } from "go-security/services/organizations/selectors";

interface Props {
	onClose: () => void;
	onFileActionPerformed: (resource: PurchaseApi | OrderApi, fileUploaded: File) => void;
	file?: File;
	saveFromFileRequest: (file: FormData) => Promise<PurchaseApi | OrderApi>;
	previewFromFileRequest: (file: FormData) => Promise<PurchaseApi | OrderApi>;
	documentPathToRedirect: string;
}

const ImportDocumentModal = ({
	onClose,
	onFileActionPerformed,
	file,
	saveFromFileRequest,
	documentPathToRedirect,
	previewFromFileRequest,
}: Props) => {
	const [fileUploaded, setFileUploaded] = useState<File | undefined>(file);
	const [saveLoading, setSaveLoading] = useState<boolean>(false);
	const [previewLoading, setPreviewLoading] = useState<boolean>(false);
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const history = useHistory();

	const onFileAction = async (setLoading: (loading: boolean) => void, actionType: "save" | "preview") => {
		if (!fileUploaded) return;

		setLoading(true);
		try {
			const data = new FormData();
			data.set("file", fileUploaded);
			if (actionType === "save") {
				// res = await apiOrganization.savePurchaseFromFile(data);
				const res = await saveFromFileRequest(data);
				history.push(`/${organization.id}/documents/${documentPathToRedirect}/${res.id}`);
			} else {
				// res = await apiOrganization.previewPurchaseFromFile(data);
				const res = await previewFromFileRequest(data);
				onFileActionPerformed(res, fileUploaded);
			}
			addSuccessFlash(t(`common.flash.${actionType === "save" ? "saved" : "completed"}`, { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const fileUploadHandler = (fileObj?: File) => setFileUploaded(fileObj);

	const saveFileHandler = () => onFileAction(setSaveLoading, "save");

	const previewFileHandler = () => onFileAction(setPreviewLoading, "preview");

	return (
		<Modal onHide={onClose} show>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.document.action.generate_document.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="purchase-modal">
					<FileUpload
						onUpdateFile={(_, fullObj) => fileUploadHandler(fullObj)}
						fileIcon={
							<FileDisplay
								icon={<EmptyImageSVG />}
								fileName={fileUploaded?.name || t("modules.document.action.drag_or_choose_file.title")}
							/>
						}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer>
				{fileUploaded && (
					<>
						<ButtonLoading loading={saveLoading} onClick={saveFileHandler}>
							{t("common.action.save", { ns: "lib" })}
						</ButtonLoading>
						<ButtonLoading loading={previewLoading} onClick={previewFileHandler}>
							{t("common.action.preview", { ns: "lib" })}
						</ButtonLoading>
					</>
				)}
				<Button variant="light" onClick={onClose}>
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default ImportDocumentModal;
